import { BoundingBox } from '@babylonjs/core/Culling/boundingBox';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';

import { hexColorByRalValue } from '../../api/core-service/wsc/colors/useColorApi';
import { Article } from '../../interfaces/Article';
import {
  isSlotLineDefinition,
  SlotDefinition,
  SlotLineDefinition,
} from '../../interfaces/SlotDefinition';
import { getMaterialUsageByName } from '../../utils/3d/Material/getMaterialUsageByName';
import { SlotNodeType, SlotNodeVariant } from '../utils/nodeUtils';
import ArticleChildNode from './articleChildNode';
import SlotLineBehavior from './slotLine/slotLineBehavior';

export default class ArticleNode {
  public name;
  private readonly _article: Article;
  readonly slots: SlotDefinition[];
  private _mesh: AbstractMesh;
  private _allMeshes: AbstractMesh[];
  private _pairedNode: AbstractMesh;
  private _isFlipped: boolean;

  constructor(article: Article, slotDefinitions: SlotDefinition[], mesh: AbstractMesh) {
    this._article = article;
    this.name = article.id;
    this.slots = slotDefinitions;
    this._mesh = mesh;
    this._isFlipped = false;
    this._allMeshes = mesh.getChildMeshes(false);
    this._allMeshes.forEach((m) => {
      m.addBehavior(new ArticleChildNode(this));
    });

    const slotLines = this.slots.filter(isSlotLineDefinition);

    if (slotLines.length) {
      this.instantiateSlotLineBehaviours(slotLines);
    }

    if (this._article.colors) {
      this.applySelectedColors();
    }
  }

  public get isFlipped(): boolean {
    return this._isFlipped;
  }

  public get id(): string {
    return this._article.id;
  }

  public set pairedNode(node: AbstractMesh) {
    this._pairedNode = node;
  }

  public get mesh(): AbstractMesh {
    return this._mesh;
  }

  public get allMeshes(): AbstractMesh[] {
    return this._allMeshes;
  }

  public get hasHeightControlPanel(): boolean {
    return this._article.definition.hasHeightControlPanel;
  }

  public get width(): number {
    return this._article.definition.width;
  }

  public dispose(): void {
    this._mesh.dispose(false, true);
    this._allMeshes.forEach((m) => m.dispose(false, true));
    this._pairedNode && this._pairedNode.dispose();
  }

  public flipMesh() {
    const currentScale = this._mesh.scaling.z;
    this._mesh.scaling.z = -currentScale;

    this._isFlipped = !this._isFlipped;
  }

  public getSlotNodesByName(name: string) {
    const slotNodes = this._mesh.getChildren(
      (child) => child.name.toLowerCase() === name.toLowerCase(),
      false,
    );

    if (!this._pairedNode) {
      return slotNodes;
    }

    return [
      ...slotNodes,
      ...this._pairedNode.getChildren(
        (child) => child.name.toLowerCase() === name.toLowerCase(),
        false,
      ),
    ];
  }

  public applySelectedColors() {
    this._article.colors.forEach((color) =>
      this.applyColor(color.affectedMaterial, color.ralValue),
    );
  }

  public updateColor(material: string, color: string): void {
    this._article.colors
      .filter((articleColor) => articleColor.affectedMaterial === material)
      .forEach((articleColor) => {
        articleColor.ralValue = color;
      });
    this.applyColor(material, color);
  }

  public applyColor(affectedMaterial: string, color: string): void {
    const articleHasColor = this._article.colors.find(
      (color) => color.affectedMaterial === affectedMaterial,
    );
    if (articleHasColor) {
      const newColor = Color3.FromHexString(hexColorByRalValue[color]).toLinearSpace();
      this._mesh
        .getScene()
        .getMaterialByTags(
          `${this._article.id} && usage=${getMaterialUsageByName(affectedMaterial) || ''}`,
          (material) => {
            if (material.getClassName() === 'PBRMaterial') {
              (material as PBRMaterial).albedoColor = newColor.clone();
            }
          },
        );
    }
  }

  public get boundingBox(): BoundingBox {
    const meshBoundaries = this.mesh.getHierarchyBoundingVectors(true, (m) =>
      this._allMeshes.includes(m),
    );

    if (this._pairedNode) {
      const secondaryBoundaries = this._pairedNode.getHierarchyBoundingVectors(true, (m) =>
        this._allMeshes.includes(m),
      );
      return new BoundingBox(meshBoundaries.min, secondaryBoundaries.max);
    }

    return new BoundingBox(meshBoundaries.min, meshBoundaries.max);
  }

  public mergePairedMeshes(): void {
    this._pairedNode.setParent(this.mesh);
  }

  private instantiateSlotLineBehaviours(slotLines: SlotLineDefinition[]) {
    const startNodes = this._mesh.getChildTransformNodes(
      false,
      (node) =>
        node.name.startsWith(SlotNodeType.LINE) && node.name.endsWith(SlotNodeVariant.START),
    );
    const endNodes = this._mesh.getChildTransformNodes(
      false,
      (node) => node.name.startsWith(SlotNodeType.LINE) && node.name.endsWith(SlotNodeVariant.END),
    );

    if (startNodes.length !== endNodes.length && startNodes.length < slotLines.length) {
      throw new Error(
        `Found ${startNodes.length} start nodes and ${endNodes.length} end nodes, but ${this._article.number} should have ${slotLines.length} slot lines`,
      );
    }

    slotLines.forEach((slotLine) => {
      const node = startNodes.find((node) => node.name.includes(slotLine.name));
      const endNodeName = node.name.replace(`_${SlotNodeVariant.START}`, `_${SlotNodeVariant.END}`);
      const endNode = endNodes.find((node) => node.name === endNodeName);

      if (!endNode) {
        throw new Error(
          `Cannot find corresponding line slot end node for start node: ${node.name}`,
        );
      }

      const slotLineNode = new SlotLineBehavior(node, endNode);
      node.addBehavior(slotLineNode);
    });
  }
}
